import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Player, PlayerEditData, volleyballPositions } from '@/types/player';

interface PlayerBasicInfoProps {
  player: Player | null;
  isEditing: boolean;
  editData: PlayerEditData;
  onEditDataChange: (data: PlayerEditData) => void;
}

export const PlayerBasicInfo = ({ player, isEditing, editData, onEditDataChange }: PlayerBasicInfoProps) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Basic Information</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Position</Label>
            {isEditing ? (
              <Select 
                value={editData.position} 
                onValueChange={(value) => onEditDataChange({...editData, position: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select position" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No position</SelectItem>
                  {volleyballPositions.map((position) => (
                    <SelectItem key={position} value={position}>
                      {position}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-sm">{player?.position || 'Not specified'}</p>
            )}
          </div>
          <div>
            <Label>Jersey Number</Label>
            {isEditing ? (
              <Input
                type="number"
                value={editData.jersey_number}
                onChange={(e) => onEditDataChange({...editData, jersey_number: e.target.value})}
                placeholder="Jersey number"
              />
            ) : (
              <p className="text-sm">
                {player?.jersey_number ? `#${player.jersey_number}` : 'Not assigned'}
              </p>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Height</Label>
            {isEditing ? (
              <Input
                value={editData.height}
                onChange={(e) => onEditDataChange({...editData, height: e.target.value})}
                placeholder="e.g., 5'10"
              />
            ) : (
              <p className="text-sm">{player?.height || 'Not specified'}</p>
            )}
          </div>
          <div>
            <Label>Graduation Year</Label>
            {isEditing ? (
              <Input
                type="number"
                value={editData.grad_year}
                onChange={(e) => onEditDataChange({...editData, grad_year: e.target.value})}
                placeholder="e.g., 2025"
              />
            ) : (
              <p className="text-sm">
                {player?.grad_year ? `Class of ${player.grad_year}` : 'Not specified'}
              </p>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Hometown</Label>
            {isEditing ? (
              <Input
                value={editData.hometown}
                onChange={(e) => onEditDataChange({...editData, hometown: e.target.value})}
                placeholder="City, State"
              />
            ) : (
              <p className="text-sm">{player?.hometown || 'Not specified'}</p>
            )}
          </div>
          <div>
            <Label>Dominant Hand</Label>
            {isEditing ? (
              <Select 
                value={editData.dominant_hand} 
                onValueChange={(value) => onEditDataChange({...editData, dominant_hand: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select hand" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not specified</SelectItem>
                  <SelectItem value="Right">Right</SelectItem>
                  <SelectItem value="Left">Left</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <p className="text-sm">{player?.dominant_hand || 'Not specified'}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};